/**
 * Export History Panel
 * Shows log of all PDF/DOCX exports with download links
 */

import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, Calendar, User, Clock, Filter, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Export type badge
const ExportTypeBadge = ({ type }) => {
  const colors = {
    pdf: 'bg-red-100 text-red-700',
    docx: 'bg-blue-100 text-blue-700',
    xlsx: 'bg-green-100 text-green-700',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${colors[type] || 'bg-gray-100 text-gray-600'}`}>
      {type}
    </span>
  );
};

// Single export item
const ExportItem = ({ exportItem, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Remove this export from history?')) return;
    
    setDeleting(true);
    await onDelete(exportItem.id);
    setDeleting(false);
  };

  return (
    <div className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate text-sm">
            {exportItem.source_name}
          </p>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
            <ExportTypeBadge type={exportItem.export_type} />
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(exportItem.created_at)}
            </span>
            {exportItem.file_size && (
              <span>{formatFileSize(exportItem.file_size)}</span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {exportItem.user_name && (
          <span className="text-xs text-gray-400 hidden md:block">
            by {exportItem.user_name}
          </span>
        )}
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
          title="Remove from history"
        >
          {deleting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <X className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
};

// Stats summary
const ExportStats = ({ stats }) => {
  if (!stats) return null;

  return (
    <div className="grid grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg">
      <div className="text-center">
        <p className="text-2xl font-bold text-gray-900">{stats.total_exports || 0}</p>
        <p className="text-xs text-gray-500">Total Exports</p>
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-red-600">{stats.by_export_type?.pdf || 0}</p>
        <p className="text-xs text-gray-500">PDFs</p>
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-blue-600">{stats.by_export_type?.docx || 0}</p>
        <p className="text-xs text-gray-500">Word Docs</p>
      </div>
    </div>
  );
};

// Main export history panel
const ExportHistoryPanel = ({ tenantId, compact = false, showStats = true }) => {
  const [exports, setExports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch exports
        const params = new URLSearchParams({ tenant_id: tenantId, limit: 50 });
        if (filter !== 'all') params.append('export_type', filter);
        
        const [exportsRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE}/export-history?${params}`, { headers: getAuthHeaders() }),
          showStats ? axios.get(`${API_BASE}/export-history/stats?tenant_id=${tenantId}&days=30`, { headers: getAuthHeaders() }) : null
        ]);
        
        setExports(exportsRes.data.exports || []);
        if (statsRes) setStats(statsRes.data);
      } catch (err) {
        console.error('Failed to load export history:', err);
      } finally {
        setLoading(false);
      }
    };

    if (tenantId) fetchData();
  }, [tenantId, filter, showStats]);

  const handleDelete = async (exportId) => {
    try {
      await axios.delete(`${API_BASE}/export-history/${exportId}?tenant_id=${tenantId}`, {
        headers: getAuthHeaders()
      });
      setExports(prev => prev.filter(e => e.id !== exportId));
      toast.success('Export removed from history');
    } catch (err) {
      toast.error('Failed to remove export');
    }
  };

  return (
    <div className="bg-white border rounded-lg" data-testid="export-history-panel">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Export History</h3>
          </div>
          <span className="text-xs text-gray-500">{exports.length} exports</span>
        </div>
        
        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'pdf', 'docx'].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                filter === type
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {type === 'all' ? 'All' : type.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      {showStats && stats && (
        <div className="p-4 border-b">
          <ExportStats stats={stats} />
        </div>
      )}

      {/* Export list */}
      <div className={`p-4 ${compact ? 'max-h-80 overflow-y-auto' : ''}`}>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : exports.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Download className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No exports yet</p>
            <p className="text-xs mt-1">Exports will appear here when you download proposals</p>
          </div>
        ) : (
          <div className="space-y-2">
            {exports.map((exp) => (
              <ExportItem
                key={exp.id}
                exportItem={exp}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportHistoryPanel;
